import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
  useCallback,
} from 'react';
import { BranchType } from '../interfaces/delivery-company/branches';
import { AuthContext } from './user-context/user-context';
import { UserType } from '../enums/users';
import axios from 'axios';
import { API_ENDPOINT } from '../configurations/global.config';
import { useSnackBar } from './snackbar';

interface SelectedBranchContextType {
  selectedBranch: BranchType | null;
  availableBranches: BranchType[];
  refreshAvailableBranches: () => Promise<BranchType[]>;
  setSelectedBranch: (branch: BranchType | null) => void;
  isLoading: boolean;
  canSelectBranch: boolean;
}

const SelectedBranchContext = createContext<SelectedBranchContextType | undefined>(
  undefined
);

interface SelectedBranchProviderProps {
  children: ReactNode;
}

export const SelectedBranchProvider = ({ children }: SelectedBranchProviderProps) => {
  const { user, config, refreshUser } = useContext(AuthContext);
  const { showMessage } = useSnackBar();

  const [selectedBranch, setSelectedBranchState] = useState<BranchType | null>(null);
  
  const [isLoading, setIsLoading] = useState(false);

  const [availableBranches, setAvailableBranches] = useState<BranchType[]>([]);


  // User is allowed to select a branch if they're Organisation or Employee
  const canSelectBranch =
    !!user && (user.type === UserType.ORGANISATION || user.type === UserType.EMPLOYEE);

  const refreshAvailableBranches = async (): Promise<BranchType[]> => {
    setIsLoading(true);
    let fetchedBranches: BranchType[] = []
    try {
      if (user?.type == UserType.EMPLOYEE) {
        await refreshUser();
        fetchedBranches = user.branches ??[] 
      } else if (user?.type == UserType.ORGANISATION) {
        fetchedBranches = (await axios.get(`${API_ENDPOINT}/api/v1/branches/?page_size=1000`, config)).data.results
      }
    } catch {
      console.error("Failed to get branches");
    } finally {
      setAvailableBranches(fetchedBranches);
      setIsLoading(false);
    }

    return fetchedBranches
  }

  // --- Branch initialization -------------------------------------------------
  useEffect(() => {
    const initializeBranches = async () => {
      // No user or not allowed to select branch => reset
      if (!user || !canSelectBranch) {
        setAvailableBranches([]);
        setSelectedBranchState(null);
        return;
      }

      const storageKey = `selected-branch-${user.id}`;
      
      // Try to restore from localStorage
      const savedBranchJson = localStorage.getItem(storageKey);
      let savedBranch: BranchType | null = null;
      if (savedBranchJson) {
        try {
          savedBranch = JSON.parse(savedBranchJson);
          // Use stored branch immediately - no need to fetch!
          setSelectedBranchState(savedBranch);
          
          // For employees, set available branches from user object (no fetch needed)
          if (user.type === UserType.EMPLOYEE) {
            setAvailableBranches(user.branches ?? []);
          } else {
            // For organisations, we'll leave availableBranches empty
            // They can be fetched lazily when branch selector is opened if needed
            setAvailableBranches([]);
          }
          
          setIsLoading(false);
          return; // Exit early - no fetch needed!
        } catch (e) {
          console.error('[Branch Context] Failed to parse saved branch', e);
          localStorage.removeItem(storageKey);
          // Continue to fetch since stored branch is invalid
        }
      }

      // Only fetch if no stored branch exists
      setIsLoading(true);
      try {
        const branches: BranchType[] = await refreshAvailableBranches();

        if (!branches.length) {
          setSelectedBranchState(null);
          localStorage.removeItem(storageKey);
          setIsLoading(false);
          return;
        }

        // No stored branch, use first branch
        const firstBranch = branches[0];
        setSelectedBranchState(firstBranch);
        localStorage.setItem(storageKey, JSON.stringify(firstBranch));
      } catch (error) {
        console.error('[Branch Context] Failed to fetch branches', error);
        showMessage('Failed to fetch branches', 'error');
        setAvailableBranches([]);
        setSelectedBranchState(null);
      } finally {
        setIsLoading(false);
      }
    };
    initializeBranches();
  }, [user?.id, user?.type, canSelectBranch, config, showMessage]);

  // --- Persist selected branch to localStorage -------------------------------
  useEffect(() => {
    if (!user) return;

    const storageKey = `selected-branch-${user.id}`;
    if (selectedBranch) {
      // Store full branch object as JSON (includes id, name, and other properties)
      localStorage.setItem(storageKey, JSON.stringify(selectedBranch));
    } else {
      localStorage.removeItem(storageKey);
    }
  }, [user, selectedBranch]);


  const setSelectedBranch = useCallback((branch: BranchType | null) => {
    setSelectedBranchState(branch);
  }, []);

  return (
    <SelectedBranchContext.Provider
      value={{
        selectedBranch,
        availableBranches,
        setSelectedBranch,
        refreshAvailableBranches,
        isLoading,
        canSelectBranch: canSelectBranch || false,
      }}
    >
      {children}
    </SelectedBranchContext.Provider>
  );
};

export const useSelectedBranch = (): SelectedBranchContextType => {
  const context = useContext(SelectedBranchContext);
  if (!context) {
    throw new Error('useSelectedBranch must be used within a SelectedBranchProvider');
  }
  return context;
};
